import {AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy} from '@angular/core';
import { Router } from '@angular/router';
import introJs from 'intro.js';
import {NgIf} from "@angular/common";
import { IntroService } from '../../services/intro.service';
import { TooltipPosition } from 'intro.js/src/packages/tooltip';
import { IntroTourActions } from '../../tours/intro-tour.actions';
import { IntroTourNavigation, IntroTourStep, IntroTourConfig, toIntroJsStep } from '../../tours/intro-tour-step';
import { ModalService } from '../../services/modal.service';
import { DropDownService } from '../../services/drop-down.service';

type TooltipOuterSide = 'top' | 'bottom' | 'left' | 'right';

@Component({
  selector: 'app-intro',
  templateUrl: './intro.component.html',
  styleUrls: ['./intro.component.scss'],
  imports: [
    NgIf
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true
})
export class IntroComponent implements OnInit, OnDestroy {
  @Input() config!: object;
  @Input() lazy!: boolean;

  @Output() doneEvent = new EventEmitter<void>();

  private sentinels: HTMLElement[] = [];
  private nextToursSection: HTMLElement | null = null;
  private nextToursAbortController: AbortController | null = null;
  private nextToursRenderGeneration = 0;
  private currentTour: ReturnType<typeof introJs> | null = null;
  private suppressTourEndCallbacks = false;
  private startGeneration = 0;
  private readonly tooltipHostClasses = [
    'intro-next-tours-host-top',
    'intro-next-tours-host-bottom',
    'intro-next-tours-host-left',
    'intro-next-tours-host-right',
  ] as const;

  constructor(
    private introService: IntroService,
    private router: Router,
    private modalService: ModalService,
    private dropdownService: DropDownService
  ) { }

  private clearSentinels() {
    this.removeNextToursSection();
    this.sentinels.forEach(sentinel => {
      sentinel.remove();
    });
    this.sentinels = [];
    IntroTourActions.releaseFocusGuard();
    document.body.classList.remove('intro-preaction-pending');
  }

  private getRemainingTours(tourId: string): IntroTourConfig[] {
    return this.introService.getApplicableConfigs().filter(tour => tour.id !== tourId);
  }

  private filterRemainingTours(tours: IntroTourConfig[], query: string): IntroTourConfig[] {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
      return tours;
    }
    return tours.filter(tour =>
      tour.title.toLowerCase().includes(normalizedQuery) ||
      (tour.description?.toLowerCase().includes(normalizedQuery) ?? false)
    );
  }

  private removeNextToursSection(): void {
    this.nextToursRenderGeneration++;
    this.nextToursAbortController?.abort();
    this.nextToursAbortController = null;
    const tooltip = (this.nextToursSection?.closest('.introjs-tooltip')
      ?? document.querySelector('.introjs-tooltip')) as HTMLElement | null;
    this.nextToursSection?.remove();
    this.nextToursSection = null;
    this.resetTooltipLayout(tooltip);
  }

  private resetTooltipLayout(tooltip: HTMLElement | null): void {
    tooltip?.classList.remove(...this.tooltipHostClasses);
  }

  private getCurrentStepElement(tour: ReturnType<typeof introJs>): HTMLElement | null {
    const stepIndex = tour.getCurrentStep();
    if (typeof stepIndex !== 'number') {
      return null;
    }

    const element = tour.getStep(stepIndex)?.element;
    if (element instanceof HTMLElement) {
      return element;
    }
    if (typeof element === 'string') {
      return document.querySelector(element);
    }
    return null;
  }

  private outerSideFromArrow(arrow: Element | null): TooltipOuterSide | null {
    if (!arrow) {
      return null;
    }

    if (arrow.classList.contains('left')) {
      return 'right';
    }
    if (arrow.classList.contains('right')) {
      return 'left';
    }
    if (arrow.classList.contains('top')) {
      return 'bottom';
    }
    if (arrow.classList.contains('bottom')) {
      return 'top';
    }

    return null;
  }

  private getTooltipSideFromClass(tooltip: HTMLElement): TooltipOuterSide {
    const cls = ` ${tooltip.className} `;
    if (/(?:^|\s)introjs-left(?:\s|$)/.test(cls)) {
      return 'left';
    }
    if (/(?:^|\s)introjs-right(?:\s|$)/.test(cls)) {
      return 'right';
    }
    if (/(?:^|\s)introjs-top(?:\s|$)/.test(cls)) {
      return 'top';
    }
    if (/(?:^|\s)introjs-bottom(?:\s|$)/.test(cls)) {
      return 'bottom';
    }

    return this.outerSideFromArrow(tooltip.querySelector('.introjs-arrow')) ?? 'bottom';
  }

  private getTooltipOuterSide(
    tour: ReturnType<typeof introJs>,
    tooltip: HTMLElement,
  ): TooltipOuterSide {
    const arrowSide = this.outerSideFromArrow(tooltip.querySelector('.introjs-arrow'));
    if (arrowSide) {
      return arrowSide;
    }

    const classSide = this.getTooltipSideFromClass(tooltip);
    const target = this.getCurrentStepElement(tour);
    if (!target) {
      return classSide;
    }

    const tooltipRect = tooltip.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const dx = (tooltipRect.left + tooltipRect.width / 2) - (targetRect.left + targetRect.width / 2);
    const dy = (tooltipRect.top + tooltipRect.height / 2) - (targetRect.top + targetRect.height / 2);

    if (Math.abs(dx) < 8 && Math.abs(dy) < 8) {
      return classSide;
    }

    if (Math.abs(dy) >= Math.abs(dx)) {
      return dy <= 0 ? 'top' : 'bottom';
    }

    return dx <= 0 ? 'left' : 'right';
  }

  private attachNextToursSection(
    tour: ReturnType<typeof introJs>,
    tooltip: HTMLElement,
    section: HTMLElement,
  ): void {
    const side = this.getTooltipOuterSide(tour, tooltip);
    tooltip.classList.remove(...this.tooltipHostClasses);
    tooltip.classList.add(`intro-next-tours-host-${side}`);
    section.classList.add(`intro-next-tours--${side}`);
    tooltip.appendChild(section);
  }

  private restoreNextToursHostClass(): void {
    const section = this.nextToursSection;
    if (!section?.isConnected) {
      return;
    }

    const tooltip = section.closest('.introjs-tooltip');
    if (!(tooltip instanceof HTMLElement)) {
      return;
    }

    const side = (['top', 'bottom', 'left', 'right'] as const).find(candidate =>
      section.classList.contains(`intro-next-tours--${candidate}`)
    );
    if (!side) {
      return;
    }

    tooltip.classList.remove(...this.tooltipHostClasses);
    tooltip.classList.add(`intro-next-tours-host-${side}`);
  }

  private scheduleNextToursRender(
    tour: ReturnType<typeof introJs>,
    tourId: string | undefined,
    totalSteps: number,
  ): void {
    if (!tourId || totalSteps === 0) {
      return;
    }

    const lastStepIndex = totalSteps - 1;
    const generation = ++this.nextToursRenderGeneration;

    const tryRender = (attempt = 0): void => {
      if (generation !== this.nextToursRenderGeneration) {
        return;
      }

      if (tour.getCurrentStep() !== lastStepIndex) {
        return;
      }

      const remainingTours = this.getRemainingTours(tourId);
      if (remainingTours.length === 0) {
        return;
      }

      const tooltip = document.querySelector('.introjs-tooltip');
      if (!tooltip?.isConnected) {
        if (attempt < 30) {
          requestAnimationFrame(() => tryRender(attempt + 1));
        }
        return;
      }

      if (tooltip.querySelector('.intro-next-tours')) {
        return;
      }

      if (this.nextToursSection) {
        this.nextToursAbortController?.abort();
        this.nextToursAbortController = null;
        this.nextToursSection.remove();
        this.nextToursSection = null;
      }

      this.renderNextToursSection(tour, remainingTours);
    };

    requestAnimationFrame(() => requestAnimationFrame(() => tryRender()));
  }

  private renderNextTourListItems(list: HTMLUListElement, tours: IntroTourConfig[]): void {
    list.replaceChildren();
    tours.forEach(remainingTour => {
      const item = document.createElement('li');
      const button = document.createElement('button');
      button.type = 'button';
      button.setAttribute('data-tour-id', remainingTour.id);

      const title = document.createElement('span');
      title.textContent = remainingTour.title;
      button.appendChild(title);

      if (remainingTour.description) {
        const description = document.createElement('p');
        description.textContent = remainingTour.description;
        button.appendChild(description);
      }

      item.appendChild(button);
      list.appendChild(item);
    });
  }

  private createSentinel(): HTMLElement {
    const sentinel = document.createElement('div');
    sentinel.style.cssText = 'position:fixed;width:1px;height:1px;opacity:0;pointer-events:none;left:0;top:0;';
    sentinel.setAttribute('data-intro-sentinel', 'true');
    document.body.appendChild(sentinel);
    this.sentinels.push(sentinel);
    return sentinel;
  }

  private mirrorRectOntoSentinel(sentinel: HTMLElement, rect: DOMRect): void {
    Object.assign(sentinel.style, {
      position: 'fixed',
      top: `${rect.top}px`,
      left: `${rect.left}px`,
      width: `${rect.width}px`,
      height: `${rect.height}px`,
      opacity: '0',
      pointerEvents: 'none',
    });
  }

  private getVisibleClipRect(el: HTMLElement): DOMRect {
    let clipTop = 0;
    let clipLeft = 0;
    let clipBottom = window.innerHeight;
    let clipRight = window.innerWidth;

    let container = el.parentElement;
    while (container && container !== document.documentElement) {
      const style = getComputedStyle(container);
      if (/(auto|scroll)/.test(style.overflowY) || /(auto|scroll)/.test(style.overflowX)) {
        const clip = container.getBoundingClientRect();
        clipTop = Math.max(clipTop, clip.top);
        clipLeft = Math.max(clipLeft, clip.left);
        clipBottom = Math.min(clipBottom, clip.bottom);
        clipRight = Math.min(clipRight, clip.right);
      }
      container = container.parentElement;
    }

    const rect = el.getBoundingClientRect();
    const top = Math.max(rect.top, clipTop);
    const left = Math.max(rect.left, clipLeft);
    const bottom = Math.min(rect.bottom, clipBottom);
    const right = Math.min(rect.right, clipRight);
    return new DOMRect(left, top, Math.max(0, right - left), Math.max(0, bottom - top));
  }

  private elementOverflowsVisibleArea(el: HTMLElement): boolean {
    const full = el.getBoundingClientRect();
    const visible = this.getVisibleClipRect(el);
    return full.height > visible.height + 1 || full.width > visible.width + 1;
  }

  private highlightRectForElement(el: HTMLElement): DOMRect {
    return this.elementOverflowsVisibleArea(el) ? this.getVisibleClipRect(el) : el.getBoundingClientRect();
  }

  private createNextToursIcon(svgPath: string): SVGSVGElement {
    const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    icon.setAttribute('viewBox', '0 0 24 24');
    icon.setAttribute('aria-hidden', 'true');
    icon.innerHTML = svgPath;
    return icon;
  }

  private renderNextToursSection(
    tour: ReturnType<typeof introJs>,
    remainingTours: IntroTourConfig[],
  ): void {
    const tooltip = document.querySelector('.introjs-tooltip');
    if (!tooltip) {
      return;
    }

    const searchIconPath = '<path fill-rule="evenodd" clip-rule="evenodd" d="M16.0422 10.5806C16.0422 13.0447 14.0447 15.0422 11.5807 15.0422C9.11664 15.0422 7.11914 13.0447 7.11914 10.5806C7.11914 8.11661 9.11664 6.11911 11.5807 6.11911C14.0447 6.11911 16.0422 8.11661 16.0422 10.5806ZM14.6325 16.2775C13.7236 16.7654 12.6845 17.0422 11.5807 17.0422C8.01207 17.0422 5.11914 14.1493 5.11914 10.5806C5.11914 7.01204 8.01207 4.11911 11.5807 4.11911C15.1493 4.11911 18.0422 7.01204 18.0422 10.5806C18.0422 12.3183 17.3563 13.8958 16.2405 15.057L19.6211 18.4376C20.0116 18.8282 20.0116 19.4613 19.6211 19.8519C19.2306 20.2424 18.5974 20.2424 18.2069 19.8519L14.6325 16.2775Z" />';
    const closeIconPath = '<path d="M5.70711 4.29289C5.31658 3.90237 4.68342 3.90237 4.29289 4.29289C3.90237 4.68342 3.90237 5.31658 4.29289 5.70711L10.5858 12L4.29289 18.2929C3.90237 18.6834 3.90237 19.3166 4.29289 19.7071C4.68342 20.0976 5.31658 20.0976 5.70711 19.7071L12 13.4142L18.2929 19.7071C18.6834 20.0976 19.3166 20.0976 19.7071 19.7071C20.0976 19.3166 20.0976 18.6834 19.7071 18.2929L13.4142 12L19.7071 5.70711C20.0976 5.31658 20.0976 4.68342 19.7071 4.29289C19.3166 3.90237 18.6834 3.90237 18.2929 4.29289L12 10.5858L5.70711 4.29289Z" />';

    const section = document.createElement('div');
    section.className = 'intro-next-tours';

    const header = document.createElement('div');
    header.className = 'intro-next-tours-header';

    const headerMain = document.createElement('div');
    headerMain.className = 'intro-next-tours-header-main';

    const heading = document.createElement('p');
    heading.className = 'intro-next-tours-heading';
    heading.textContent = 'Continue learning...';

    const search = document.createElement('div');
    search.className = 'intro-next-tours-search';

    const searchFieldIcon = this.createNextToursIcon(searchIconPath);
    searchFieldIcon.classList.add('intro-next-tours-search-icon');

    const searchInput = document.createElement('input');
    searchInput.type = 'text';
    searchInput.className = 'intro-next-tours-search-input';
    searchInput.placeholder = 'Search tours...';
    searchInput.setAttribute('aria-hidden', 'true');
    searchInput.tabIndex = -1;

    search.appendChild(searchFieldIcon);
    search.appendChild(searchInput);

    const searchToggle = document.createElement('button');
    searchToggle.type = 'button';
    searchToggle.className = 'intro-next-tours-search-toggle';
    searchToggle.setAttribute('aria-label', 'Search tours');
    searchToggle.setAttribute('aria-expanded', 'false');
    searchToggle.appendChild(this.createNextToursIcon(searchIconPath));

    headerMain.appendChild(heading);
    headerMain.appendChild(search);
    header.appendChild(headerMain);
    header.appendChild(searchToggle);

    const emptyMessage = document.createElement('p');
    emptyMessage.className = 'intro-next-tours-empty-message';
    emptyMessage.textContent = 'No tours match your search.';
    emptyMessage.hidden = true;

    const list = document.createElement('ul');
    list.className = 'intro-next-tours-list';
    this.renderNextTourListItems(list, remainingTours);

    section.appendChild(header);
    section.appendChild(emptyMessage);
    section.appendChild(list);

    const abortController = new AbortController();
    this.nextToursAbortController = abortController;
    const { signal } = abortController;

    const updateFilteredTours = () => {
      const filteredTours = this.filterRemainingTours(remainingTours, searchInput.value);
      this.renderNextTourListItems(list, filteredTours);
      emptyMessage.hidden = !(searchInput.value.trim() && filteredTours.length === 0);
    };

    const setSearchExpanded = (expanded: boolean) => {
      header.classList.toggle('search-expanded', expanded);
      searchToggle.replaceChildren(
        this.createNextToursIcon(expanded ? closeIconPath : searchIconPath)
      );
      searchToggle.setAttribute('aria-label', expanded ? 'Close search' : 'Search tours');
      searchToggle.setAttribute('aria-expanded', String(expanded));
      searchInput.setAttribute('aria-hidden', String(!expanded));
      searchInput.tabIndex = expanded ? 0 : -1;
      if (!expanded) {
        searchInput.value = '';
        updateFilteredTours();
      } else {
        IntroTourActions.releaseFocusGuard();
        setTimeout(() => searchInput.focus(), 200);
      }
    };

    const stopIntroPointerCapture = (event: Event) => {
      event.stopPropagation();
    };

    section.addEventListener('mousedown', stopIntroPointerCapture, { signal });
    search.addEventListener('mousedown', stopIntroPointerCapture, { signal });

    searchToggle.addEventListener('click', (event) => {
      event.stopPropagation();
      setSearchExpanded(!header.classList.contains('search-expanded'));
    }, { signal });

    searchInput.addEventListener('input', () => updateFilteredTours(), { signal });

    section.addEventListener('click', (event) => {
      const target = (event.target as HTMLElement).closest('[data-tour-id]');
      if (!(target instanceof HTMLElement)) {
        return;
      }
      const nextTourId = target.getAttribute('data-tour-id');
      if (!nextTourId) {
        return;
      }
      const nextTour = this.introService.getApplicableConfigs().find(t => t.id === nextTourId);
      if (!nextTour) {
        return;
      }
      void this.switchToTour(nextTour);
    }, { signal });

    this.nextToursSection = section;
    this.attachNextToursSection(tour, tooltip as HTMLElement, section);
  }

  private async runCurrentTourCleanup(): Promise<void> {
    const { onExit } = (this.config ?? {}) as { onExit?: () => void | Promise<void> };
    if (onExit) {
      await Promise.resolve(onExit());
    }
  }

  private async stopCurrentTour(
    options: { skipEndCallbacks?: boolean; runCleanup?: boolean } = {},
  ): Promise<void> {
    const tour = this.currentTour;
    if (!tour) {
      this.clearSentinels();
      return;
    }

    this.suppressTourEndCallbacks = options.skipEndCallbacks === true;
    this.currentTour = null;
    this.removeNextToursSection();

    try {
      await Promise.resolve(tour.exit());
    } catch {
      // Intro.js may already have exited.
    }

    this.clearSentinels();

    if (options.skipEndCallbacks) {
      if (options.runCleanup) {
        await this.runCurrentTourCleanup();
      } else {
        this.modalService.closeAll();
        this.dropdownService.hideMenu();
      }
    }

    await new Promise<void>(resolve => setTimeout(resolve, 0));
    this.suppressTourEndCallbacks = false;
  }

  private async switchToTour(nextTour: IntroTourConfig): Promise<void> {
    this.removeNextToursSection();
    await this.stopCurrentTour({ skipEndCallbacks: true, runCleanup: true });
    this.introService.setIntroConfig(null);
    this.introService.setIntroConfig({ ...nextTour.config, tourId: nextTour.id });
  }

  ngOnInit() {
    if (this.config) {
      return;
    }
    this.introService.introConfig$.subscribe((config) => {
      if (config) {
        this.config = config;
        this.start();
      }
    });
  }

  ngOnDestroy() {
    void this.stopCurrentTour({ skipEndCallbacks: true });
  }

  async start() {
    const generation = ++this.startGeneration;
    await this.stopCurrentTour({ skipEndCallbacks: true });
    if (generation !== this.startGeneration) {
      return;
    }
    this.clearSentinels();
    const config = this.config as {
      steps?: IntroTourStep[];
      onComplete?: () => void;
      onExit?: () => void;
      tourId?: string;
    };
    const { onComplete, onExit, tourId, ...introOptions } = config;

    const tourOrigin: IntroTourNavigation = {
      route: IntroTourActions.normalizeRoutePath(this.router.url),
      tab: IntroTourActions.getActiveTabName() ?? undefined,
    }

    const selectorByIndex = new Map<number, string>();
    const positionByIndex = new Map<number, TooltipPosition>();
    const introSteps = (config.steps ?? []).map((step, index) => {
      if (typeof step.element !== 'string') {
        return toIntroJsStep(step, step.element as HTMLElement);
      }
      selectorByIndex.set(index, step.element);
      if (step.position) {
        positionByIndex.set(index, step.position as TooltipPosition);
      }
      const el = document.querySelector(step.element) as HTMLElement | null;
      if (!el) {
        return toIntroJsStep(step, step.element);
      }
      if (el && !this.elementOverflowsVisibleArea(el)) {
        const sentinel = this.createSentinel();
        this.mirrorRectOntoSentinel(sentinel, this.highlightRectForElement(el));
        return toIntroJsStep(step, sentinel);
      }

      return toIntroJsStep(step, step.element);
    });

    const tour = introJs().setOptions({
      ...introOptions,
      steps: introSteps,
      scrollToElement: false,
      scrollTo: 'off',
      scrollPadding: 0,
      exitOnOverlayClick: false,
      disableInteraction: true,
    } as object);

    const originalRefresh = tour.refresh.bind(tour);
    tour.refresh = ((refreshSteps?: boolean) => {
      const result = originalRefresh(refreshSteps);
      this.restoreNextToursHostClass();
      this.scheduleNextToursRender(tour, tourId, introSteps.length);
      return result;
    }) as typeof tour.refresh;

    const bindStepTarget = (stepIndex: number) => {
      const step = tour.getStep(stepIndex);
      const selector = selectorByIndex.get(stepIndex) ?? null;
      const el = selector ? document.querySelector(selector) as HTMLElement | null : null;
      if (!el || !step) {
        return;
      }

      const overflows = this.elementOverflowsVisibleArea(el);
      const rect = this.highlightRectForElement(el);
      const isSentinel = step.element instanceof HTMLElement ? step.element.hasAttribute('data-intro-sentinel') : false;
      if (overflows) {
        if (!isSentinel) {
          step.element = this.createSentinel();
        }
        this.mirrorRectOntoSentinel(step.element as HTMLElement, rect);
        return;
      }
      step.element = el;
    };

    const preSizeSentinelForStep = (stepIndex: number) => {
      const step = tour.getStep(stepIndex);
      const target = step?.element;
      if (!(target instanceof HTMLElement) || !target.hasAttribute('data-intro-sentinel')) {
        return;
      }
      const selector = selectorByIndex.get(stepIndex) ?? null;
      const el = selector ? document.querySelector(selector) as HTMLElement | null : null;
      if (el) {
        this.mirrorRectOntoSentinel(target, this.highlightRectForElement(el));
        return;
      }
      if (stepIndex > 0) {
        const prev = tour.getStep(stepIndex - 1)?.element;
        if (prev instanceof HTMLElement) {
          this.mirrorRectOntoSentinel(target, prev.getBoundingClientRect());
        }
      }
    };

    const getDestinationStepIndex = (previousStep: number | undefined, direction: 'forward' | 'backward') => {
      if(direction === 'forward') {
        return previousStep === undefined ? 0 : previousStep + 1;
      }
      return previousStep === undefined ? 0 : previousStep - 1;
    }

    tour.onComplete(() => {
      this.clearSentinels();
      if (this.currentTour === tour) {
        this.currentTour = null;
      }
      if (!this.suppressTourEndCallbacks) {
        onComplete?.();
      }
    });
    tour.onExit(() => {
      this.clearSentinels();
      if (this.currentTour === tour) {
        this.currentTour = null;
      }
      if (!this.suppressTourEndCallbacks) {
        onExit?.();
      }
    });

    const revealStepTransition = () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          document.body.classList.remove('intro-preaction-pending');
        });
      });
    };

    tour.onAfterChange(() => {
      revealStepTransition();

      const currentStep = tour.getCurrentStep();
      this.removeNextToursSection();

      if (typeof currentStep === 'number') {
        preSizeSentinelForStep(currentStep + 1);
        this.scheduleNextToursRender(tour, tourId, introSteps.length);
      }
    });

    tour.onBeforeChange(async(_targetElement, previousStep, direction) => {
      let destinationStepIndex = -1;

      for(let i = 0; i < (config.steps?.length ?? 0); i++) {
        if(tour.getStep(i)?.element === _targetElement) {
          destinationStepIndex = i;
          break;
        }
      }

      if(destinationStepIndex < 0) {
        destinationStepIndex = getDestinationStepIndex(previousStep, direction);
      }

      const currentStepConfig = config.steps?.[destinationStepIndex];
      document.body.classList.add('intro-preaction-pending');

      let proceeding = false;
      try {
        const ready = await IntroTourActions.runStepSetup(config.steps ?? [], destinationStepIndex, previousStep, tourOrigin, this.router, this.modalService, this.dropdownService);
        if (!ready) {
          return false;
        }

        const selector = selectorByIndex.get(destinationStepIndex) ?? null;
        if (selector) {
          await IntroTourActions.scrollIntoNearestScrollParent(selector);
        }

        const step = tour.getStep(destinationStepIndex);
        bindStepTarget(destinationStepIndex);
        const position = positionByIndex.get(destinationStepIndex) ?? (currentStepConfig?.position as TooltipPosition | undefined);
        if (position && step) {
          step.position = position;
        }

        await new Promise<void>(r =>
          requestAnimationFrame(() => requestAnimationFrame(() => r()))
        );
        bindStepTarget(destinationStepIndex);
        tour.refresh();
        proceeding = true;
        return true;
      } finally {
        if (!proceeding) {
          document.body.classList.remove('intro-preaction-pending');
        }
      }
    });

    this.currentTour = tour;

    tour.goToStep = async (stepNumber: number) => {
      const targetIndex = stepNumber - 1;
      if (targetIndex < 0 || targetIndex >= introSteps.length) {
        return tour;
      }

      let current = tour.getCurrentStep();
      if (current === undefined) {
        await tour.start();
        current = tour.getCurrentStep();
      }

      while ((current ?? 0) < targetIndex) {
        await tour.nextStep();
        current = tour.getCurrentStep();
      }
      while ((current ?? 0) > targetIndex) {
        await tour.previousStep();
        current = tour.getCurrentStep();
      }

      return tour;
    };

    if (generation !== this.startGeneration) {
      this.suppressTourEndCallbacks = true;
      try {
        await Promise.resolve(tour.exit());
      } catch {
        // Intro.js may already have exited.
      }
      if (this.currentTour === tour) {
        this.currentTour = null;
      }
      this.suppressTourEndCallbacks = false;
      return;
    }

    tour.start();
  }
}
